import AppBaseStore from './baseStore'
import { useDjango } from '@/composables/useDjango'
import type { BaseStoreActions, BaseStoreType } from '@/types/storeTypes'
import type { GIPointCollection, GroundInvestigationPoint } from '@/stores/type/giPoint.type'
import API from '@/api/apiUrls'
import { ClassificationData } from '@/stores/type/classifications.type.ts'
import type { ImportErrors } from '@/composables/useGIDataSpeckleImport'

const { api } = useDjango()

type State = BaseStoreType<GIPointCollection>

const baseUrl = API.GI_DATA.GI_DATA_UPLOAD_COLLECTIONS.INDEX
const baseStore: BaseStoreType<GIPointCollection> = new AppBaseStore(baseUrl, api, null, 'id')
const baseStoreActions: BaseStoreActions<GIPointCollection> = baseStore.actions
export const GEOLOGY_CODE = 'GeologyCode'
export const GEOLOGY_CODE_2 = 'GeologyCode2'

export const useGIDataUploadCollectionStore = defineStore({
  id: 'giDataUploadCollection',
  state: (): State => ({
    ...baseStore.state,
    loading: false,
    filterFields: ['name'],
    showSelectMap: {} as Record<number, boolean>,
    showUploadDialog: false,
    showImportDialog: false,
    showClassificationUploadDialog: false,
    selectedItems: [] as GIPointCollection[],
    importableProjects: [],
    selectedGICollectionForUploadClassification: null,
    selectedFile: null,
    loadingUploadClassification: false,
    classificationValueOverview: [] as ClassificationData[],
    selectedClasssificationColumnName: GEOLOGY_CODE,
    importFromSpeckleDialog: false,
    importWarningsDialog: false,
    importWarnings: {} as ImportErrors
  }),
  actions: {
    ...baseStoreActions,
    resetAll() {
      this.$reset()
    },
    toggleUploadDialog(item: GIPointCollection = null) {
      if (item) {
        this.currentItem = item
      }
      this.showUploadDialog = !this.showUploadDialog
    },
    toggleImportFromSpeckleDialog(value: boolean | undefined = undefined) {
      if (typeof value === 'undefined') {
        this.importFromSpeckleDialog = !this.importFromSpeckleDialog
        return
      }
      this.importFromSpeckleDialog = value
    },
    showImportWarnings(warnings: ImportErrors) {
      this.importWarnings = warnings
      this.importWarningsDialog = true
    },
    closeImportWarnings() {
      this.importWarningsDialog = false
      this.importWarnings = {}
    },
    toggleSelectedGIPointCollection(giPointCollection: GIPointCollection) {
      const index = this.selectedItems.findIndex((item) => item.id === giPointCollection.id)
      if (index !== -1) {
        this.selectedItems.splice(index, 1)
      } else {
        this.selectedItems.push(giPointCollection)
      }
    },
    toggleImportDialog() {
      this.showImportDialog = !this.showImportDialog
    },
    toggleClassificationUploadDialog() {
      this.showClassificationUploadDialog = !this.showClassificationUploadDialog
    },
    selectAllGIPointCollections() {
      this.selectedItems = [...this.items]
    },
    clearSelectedGIPointCollections() {
      this.selectedItems = []
    },
    async uploadClassificationFile() {
      const projectSore = useProjectStore()
      try {
        if (
          !this.selectedGICollectionForUploadClassification ||
          !this.selectedClasssificationColumnName ||
          !this.selectedFile
        ) {
          return
        }
        const formData = new FormData()
        formData.append(
          'gi_data_upload_collection_id',
          this.selectedGICollectionForUploadClassification
        )
        formData.append('primary_class_column_name', this.selectedClasssificationColumnName)
        formData.append('file', this.selectedFile)
        formData.append('project', projectSore.currentItem.id)
        const url = API.GI_DATA.GI_DATA_UPLOAD_COLLECTIONS.UPLOAD_CLASSIFICATION_DATA
        const response = await api.service(url).post(formData)
        this.classificationValueOverview = response
        this.showClassificationUploadDialog = false
        this.resetClassificationUploadData()
      } finally {
        this.loadingUploadClassification = false
      }
    },
    async fetchClassificationValueOverview(giDataUploadCollectionID: number) {
      try {
        this.loadingUploadClassification = true
        const url = API.GI_DATA.GI_DATA_UPLOAD_COLLECTIONS.GET_CLASSIFICATION_DATA
        const response = await api
          .service(url)
          .find({ query: { gi_data_upload_collection_id: giDataUploadCollectionID } })
        this.classificationValueOverview = response.data
      } finally {
        this.loadingUploadClassification = false
      }
    },
    resetClassificationUploadData() {
      this.selectedFile = null
    },
    async updateClassificationColor(color: string, primaryClassName: string) {
      const url = API.GI_DATA.GI_DATA_UPLOAD_COLLECTIONS.UPDATE_CLASSIFICATION_DATA
      const response = await api.service(url).post({
        project: useProjectStore().currentItem.id,
        gi_data_upload_collection_id: this.selectedGICollectionForUploadClassification,
        primary_class_column_name: this.selectedClasssificationColumnName,
        primary_class_name: primaryClassName,
        color_code: color
      })
    },
    async downloadClassificationUploadTemplate() {
      const gi_data_upload_collection_id = this.selectedGICollectionForUploadClassification
      const primary_class_column_name = this.selectedClasssificationColumnName
      const params = new URLSearchParams({
        gi_data_upload_collection_id,
        primary_class_column_name
      })
      const url = API.GI_DATA.GI_DATA_UPLOAD_COLLECTIONS.DOWNLOAD_TEMPLATE + `?${params.toString()}`
      await api.service(url).downloadBlob(null, { fileName: 'classification-upload-template.xlsm' })
    }
  },
  getters: {
    ...baseStore.getters,
    selectedItemIDs: (state: State) => {
      return state.selectedItems.map((item) => item.id)
    },
    giDataCollectionByID: (state: State) => (id: number) => {
      return state.items.find((item) => item.id === id)
    },
    giDataUploadCollectionByName: (state: State) => (name: string) => {
      return state.items.find((item: GIPointCollection) =>
        item.gipoint_set.features.find(
          (giPoint: GroundInvestigationPoint) => giPoint.properties.location_id === name
        )
      )
    },
    locationIdColorMapping(state: State) {
      const result: Record<string, string> = {}
      for (const collection of state.items) {
        for (const giPoint in collection.gipoint_set.features) {
          const locationId = collection.gipoint_set.features[giPoint].properties.location_id
          result[locationId] = collection.collection_color
        }
      }
      return result
    },
    canUploadClassification(state: State) {
      return (
        state.selectedGICollectionForUploadClassification &&
        state.selectedClasssificationColumnName &&
        state.selectedFile
      )
    },
    displayClassificationOverview(state: State) {
      return state.classificationValueOverview.filter(
        (item: any) => item.primary_class_column_name === state.selectedClasssificationColumnName
      )
    },
    canDownloadTemplate(state: State) {
      return (
        state.selectedClasssificationColumnName && state.selectedGICollectionForUploadClassification
      )
    }
  }
})
